import { useState } from 'react';
import toast from 'react-hot-toast';
import { capstoneService } from '../../services';
import GitHubMetadata from './GitHubMetadata';
import { 
  LinkIcon, 
  CodeBracketIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  ArrowTopRightOnSquareIcon 
} from '@heroicons/react/24/outline';

export default function GitHubSubmission({ 
  value = '', 
  onChange, 
  onValidate,
  label = "GitHub Repository URL",
  placeholder = "https://github.com/username/repository",
  helpText = "Submit your project code as a GitHub repository",
  required = false 
}) {
  const [githubUrl, setGithubUrl] = useState(value);
  const [isValid, setIsValid] = useState(null);
  const [repoInfo, setRepoInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);

  const validateGitHubUrl = (url) => {
    if (!url) {
      setIsValid(null);
      setRepoInfo(null);
      return false;
    }

    // GitHub URL patterns
    const patterns = [
      /^https?:\/\/(www\.)?github\.com\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)\/?$/,
      /^git@github\.com:([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)\.git$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const owner = match[match.length - 2];
        const repo = match[match.length - 1].replace('.git', '');
        
        const info = {
          owner,
          repo,
          fullName: `${owner}/${repo}`,
          url: `https://github.com/${owner}/${repo}`,
          codeUrl: `https://github.com/${owner}/${repo}/tree/main`,
          commitsUrl: `https://github.com/${owner}/${repo}/commits/main`,
          readmeUrl: `https://github.com/${owner}/${repo}#readme`,
        };

        setRepoInfo(info);
        setIsValid(true);
        if (onValidate) onValidate(true, info);
        return true;
      }
    }

    setIsValid(false);
    setRepoInfo(null);
    if (onValidate) onValidate(false, null);
    return false;
  };

  const handleChange = (e) => {
    const url = e.target.value;
    setGithubUrl(url);
    validateGitHubUrl(url);
    if (onChange) onChange(url);
  };

  const handlePaste = (e) => {
    // Clean up pasted URLs
    setTimeout(() => {
      const url = githubUrl.trim();
      setGithubUrl(url);
      validateGitHubUrl(url);
      if (onChange) onChange(url);
    }, 0);
  };

  const handleVerify = async () => {
    if (!isValid || !githubUrl) {
      toast.error('Please enter a valid GitHub URL first');
      return;
    }

    try {
      setVerifying(true);
      const data = await capstoneService.verifyGithubRepo(githubUrl);
      
      if (data.verified) {
        setMetadata(data);
        setShowMetadata(true);
        toast.success('Repository verified successfully! ✓');
      } else {
        toast.error(data.error || 'Failed to verify repository');
        setMetadata(null);
        setShowMetadata(false);
      }
    } catch (err) {
      console.error('Verification error:', err);
      toast.error(err.response?.data?.message || 'Failed to verify repository');
      setMetadata(null);
      setShowMetadata(false);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Help Text */}
      {helpText && (
        <p className="text-sm text-gray-600">{helpText}</p>
      )}

      {/* Input Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
          </svg>
        </div>
        <input
          type="url"
          value={githubUrl}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={() => validateGitHubUrl(githubUrl)}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-10 py-3 border ${
            isValid === null
              ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              : isValid
              ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
              : 'border-red-500 focus:ring-red-500 focus:border-red-500'
          } rounded-lg shadow-sm`}
        />
        {isValid !== null && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {isValid ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            )}
          </div>
        )}
      </div>

      {/* Validation Messages */}
      {isValid === false && githubUrl && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Invalid GitHub URL</p>
            <p className="text-xs mt-1">
              Please enter a valid GitHub repository URL like:<br />
              <code className="text-xs bg-red-100 px-1 py-0.5 rounded">
                https://github.com/username/repository
              </code>
            </p>
          </div>
        </div>
      )}

      {/* Verify Button */}
      {isValid && !showMetadata && (
        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {verifying ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying Repository...
            </>
          ) : (
            <>
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Verify Repository & Fetch Stats
            </>
          )}
        </button>
      )}

      {/* Repository Preview */}
      {isValid && repoInfo && (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="h-6 w-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {repoInfo.fullName}
                </p>
                <p className="text-xs text-gray-500">Repository detected</p>
              </div>
            </div>
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <a
              href={repoInfo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              <CodeBracketIcon className="h-4 w-4" />
              View Code
              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
            <a
              href={repoInfo.commitsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              <LinkIcon className="h-4 w-4" />
              Commits
              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
            <a
              href={repoInfo.readmeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              📄 README
              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}

      {/* GitHub Metadata Display */}
      {showMetadata && metadata && (
        <div className="mt-4">
          <GitHubMetadata metadata={metadata} showTitle={false} />
        </div>
      )}

      {/* Instructions */}
      <details className="text-sm">
        <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
          How to submit your GitHub repository
        </summary>
        <div className="mt-3 space-y-2 text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="font-medium text-gray-900">📝 Steps:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Create a GitHub repository for your project</li>
            <li>Push your code to the repository</li>
            <li>Make sure your repository is <strong>public</strong> or accessible to instructors</li>
            <li>Include a README.md with setup instructions</li>
            <li>Copy the repository URL (e.g., https://github.com/username/project)</li>
            <li>Paste the URL in the field above</li>
          </ol>
          <p className="mt-3 text-xs">
            💡 <strong>Tip:</strong> Make sure your latest changes are pushed before submitting!
          </p>
        </div>
      </details>
    </div>
  );
}
